/**
 * Custom hook for managing hold selection state
 */

import { useState, useEffect } from 'react';
import type { HoldList } from 'api-client/models';
import { isHoldActionable, isHoldDetailSelectable } from '../utils/holdSelection';

interface UseHoldSelectionResult {
  selectedHoldIds: Set<string>;
  selectedHoldForDetails: HoldList | null;
  handleSelectHold: (holdId: string) => void;
  handleSelectAllHolds: (holds: HoldList[]) => void;
  handleSelectHoldForDetails: (hold: HoldList) => void;
  setSelectedHoldForDetails: (hold: HoldList | null) => void;
}

/**
 * Hook to manage hold selection (checkboxes and radio buttons)
 * Auto-selects first confirmed hold on mount if available
 */
export const useHoldSelection = (
  holds: HoldList[],
  showId?: string,
): UseHoldSelectionResult => {
  const [selection, setSelection] = useState<{
    scope: string | undefined; ids: Set<string>; detailId: string | null;
  }>({ scope: showId, ids: new Set(), detailId: null });
  const currentScope = selection.scope === showId;
  const actionableIds = new Set(holds.filter(isHoldActionable).map((hold) => hold.id));
  const selectedHoldIds = new Set(
    currentScope ? [...selection.ids].filter((id) => actionableIds.has(id)) : [],
  );
  const selectedHoldForDetails = holds.find((hold) =>
    currentScope && hold.id === selection.detailId && isHoldDetailSelectable(hold))
    ?? holds.find((hold) => hold.status === 'confirmed') ?? null;

  useEffect(() => {
    setSelection((previous) => {
      const sameScope = previous.scope === showId;
      const ids = new Set(sameScope ? [...previous.ids].filter((id) =>
        holds.some((hold) => hold.id === id && isHoldActionable(hold))) : []);
      const detail = holds.find((hold) =>
        sameScope && hold.id === previous.detailId && isHoldDetailSelectable(hold))
        ?? holds.find((hold) => hold.status === 'confirmed');
      const detailId = detail?.id ?? null;
      if (sameScope && detailId === previous.detailId && ids.size === previous.ids.size) return previous;
      return { scope: showId, ids, detailId };
    });
  }, [holds, showId]);

  /**
   * Toggle checkbox selection for a hold
   */
  const handleSelectHold = (holdId: string) => {
    setSelection((previous) => {
      const ids = new Set(previous.scope === showId ? previous.ids : []);
      if (ids.has(holdId)) ids.delete(holdId);
      else if (actionableIds.has(holdId)) ids.add(holdId);
      return { scope: showId, ids, detailId: selectedHoldForDetails?.id ?? null };
    });
  };

  /**
   * Select or deselect all holds (for "select all" checkbox)
   */
  const handleSelectAllHolds = (candidates: HoldList[]) => {
    const eligible = candidates.filter((hold) => actionableIds.has(hold.id)).map((hold) => hold.id);
    const allSelected = eligible.length > 0 && eligible.every((id) => selectedHoldIds.has(id));
    setSelection({
      scope: showId, ids: new Set(allSelected ? [] : eligible),
      detailId: selectedHoldForDetails?.id ?? null,
    });
  };

  /**
   * Select a hold for details view (radio button selection)
   */
  const handleSelectHoldForDetails = (hold: HoldList) => {
    setSelection((previous) => ({
      ...previous, scope: showId,
      detailId: holds.find((current) => current.id === hold.id && isHoldDetailSelectable(current))?.id ?? null,
    }));
  };

  return {
    selectedHoldIds,
    selectedHoldForDetails,
    handleSelectHold,
    handleSelectAllHolds,
    handleSelectHoldForDetails,
    setSelectedHoldForDetails: (hold) => {
      if (hold) handleSelectHoldForDetails(hold);
      else setSelection((previous) => ({ ...previous, scope: showId, detailId: null }));
    },
  };
};
